import React, { useState, useEffect, useCallback } from 'react';
import apiCall from '../helper/apiCall'; 

const PurchaseOrderSearchForm = () => {
  const [manufacturerNames, setManufacturerNames] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);
  
  // State for search criteria
  const [searchRef, setSearchRef] = useState('');
  
  // State for the retrieved Purchase Order details
  const [poResult, setPoResult] = useState(null);
  const [searchStatus, setSearchStatus] = useState(''); // 'idle', 'loading', 'found', 'not_found'


  // --- Data Fetching for Dropdown Options (Unchanged, copied from Create Form) ---

  const fetchManufacturerNames = useCallback(async () => {
    try {
      const result = await apiCall('getManufacturerNames');
      setManufacturerNames(result.data);
    } catch (error) {
      console.error("Failed to fetch getManufacturerNames:", error);
    }
  }, []);

  const fetchCategoryOptions = useCallback(async () => {
    try {
      const result = await apiCall('getCategories');
      setCategoryOptions(result.data);
    } catch (error) {
      console.error("Failed to fetch getCategories:", error);
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
        setIsLoading(true);
        await Promise.all([
            fetchManufacturerNames(),
            fetchCategoryOptions(),
        ]);
        setIsLoading(false);
        setSearchStatus('idle');
    };
    loadInitialData();
  }, [fetchManufacturerNames, fetchCategoryOptions]);

  // --- Search Logic ---

  const handleSearchChange = (e) => {
    setSearchRef(e.target.value);
    setPoResult(null); // Clear previous results when search term changes
    setSearchStatus('idle');
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!searchRef.trim()) return;

    setSearchStatus('loading');
    setPoResult(null);

    try {
      // NOTE: Assume you have a new API endpoint 'getPurchaseOrderByRef'
      const response = await apiCall('getPurchaseOrderByRef', { po_reference_number: searchRef.trim() });
      
      if (response.success && response.data) {
        setPoResult(response.data); // Assuming response.data contains order header and items
        setSearchStatus('found');
      } else {
        setSearchStatus('not_found');
      }
    } catch (error) {
      console.error("Error searching for PO:", error);
      setSearchStatus('not_found');
    }
  };

  // --- Calculations for Display (copied from Create Form) ---
  const calculateSubtotal = useCallback((items) => {
    return items.reduce((sum, item) => {
      const quantity = Number(item.purchase_quantity || 0);
      const price = Number(item.purchase_price || 0);
      return sum + (quantity * price);
    }, 0);
  }, []);
  
  const subtotal = poResult ? calculateSubtotal(poResult.purchase_order_items || []) : 0.0;
  const discount = poResult ? Number(poResult.discount || 0) : 0.0;
  const totalAmount = Math.max(0, subtotal - discount);

  // --- Loading State ---
  if (isLoading) {
    return (
      <div className="text-center text-indigo-600">Loading initial data...</div>
    );
  }

  // --- Render ---
  return (
    <form onSubmit={handleSearchSubmit}>
      <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-2">Search Purchase Order</h2>

      {/* Search Input Section */}
      <div className="flex gap-4 mb-8 items-end">
        <div className="flex-grow">
          <label htmlFor="search_ref" className="block text-sm font-medium text-gray-700">
            Purchase Order Reference Number
          </label>
          <input
            type="text"
            id="search_ref"
            name="search_ref"
            value={searchRef}
            onChange={handleSearchChange}
            required
            placeholder="e.g., M101-20251208-001"
            className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={searchStatus === 'loading'}
          className={`py-2 px-4 border border-transparent text-sm font-medium rounded-md shadow-sm text-white transition-colors ${
            searchStatus === 'loading'
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
          }`}
        >
          {searchStatus === 'loading' ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Search Status Display */}
      {searchStatus === 'not_found' && (
        <div className="mb-6 p-3 bg-red-100 border-l-4 border-red-500 text-red-700">
          Order not found for reference: **{searchRef}**.
        </div>
      )}

      {/* --- Display Result (Only if found) --- */}
      {poResult && searchStatus === 'found' && (
        <div>
          <h3 className="text-xl font-semibold text-gray-700 mb-4">
            Order Details: {poResult.po_reference_number}
          </h3>

          {/* Header Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 bg-gray-50 p-4 rounded-md border">
            <div>
              <label className="block text-sm font-medium text-gray-700">Manufacturer</label>
              <select
                value={poResult.manufacturer_id}
                disabled 
                className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm disabled:bg-gray-100"
              >
                <option value="">Select Manufacturer</option>
                {manufacturerNames.map(manufacturer => (
                  <option key={manufacturer.manufacturer_id} value={manufacturer.manufacturer_id}>
                    {manufacturer.manufacturer_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Purchase Date</label>
              <input
                
                value={poResult.purchase_date}
                disabled
                className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm sm:text-sm disabled:bg-gray-100"
              />
            </div>
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700">Discount ($)</label>
              <input
                type="number"
                value={poResult.discount}
                disabled
                className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm sm:text-sm disabled:bg-gray-100"
              />
            </div>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              {/* Table Header (Same as Create Form) */}
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Ref.</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Quantity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Price (Unit)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(poResult.purchase_order_items || []).map((item, index) => {
                  const itemTotal = (item.purchase_quantity * item.purchase_price).toFixed(2);
                  
                  // Find category name for display
                  const category = categoryOptions.find(c => c.category_id === Number(item.category_id));
                  const categoryName = category ? category.category_name : `ID: ${item.category_id}`;

                  return (
                    <tr key={index}>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                      
                      {/* Category Name Display */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                        {categoryName}
                      </td>
                      
                      {/* Item Reference */}
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-gray-600">
                        {item.po_item_reference}
                      </td>

                      {/* Quantity Display (Disabled Input) */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <input type="number" value={item.purchase_quantity} disabled className="w-full disabled:bg-gray-100 border-gray-300 rounded-md shadow-sm sm:text-sm" />
                      </td>

                      {/* Price Display (Disabled Input) */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <input type="number" value={item.purchase_price} disabled className="w-full disabled:bg-gray-100 border-gray-300 rounded-md shadow-sm sm:text-sm" />
                      </td>

                      {/* Calculated Item Total */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {itemTotal}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary and Totals (Same as Create Form) */}
          <div className="mt-8 pt-4 border-t border-gray-200 flex justify-end">
            <div className="w-full md:w-1/2 space-y-2">
              <div className="flex justify-between text-sm font-medium text-gray-600">
                <span>Subtotal:</span>
                <span>{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium text-gray-600">
                <span>Discount:</span>
                <span>{discount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t">
                <span>Total Amount:</span>
                <span>{totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default PurchaseOrderSearchForm;